import { ImageLabelLineProps } from 'components/shared'
import { SelectOption } from 'components/shared/Select'
import { action, computed, observable } from 'mobx'
import { BrandDTO, PersonDTO, Project, ProjectDTO, UserDTO, UserRoleDTO } from 'models'
import { GENERAL_STORE } from 'store/GeneralStore'
import { PROJECTS_TABLE_STORE } from 'store/ProjectsTableStore'
import { PROJECT_STORE } from 'store/ProjectStore'
import { RootStore } from 'store/RootStore'
import { Api, getMainImageSrcForObj, isEmailValid } from 'utilities'
import {
  LinkTo,
  personEditRoleId,
  roleIdsWithNoProjectsAssignmentOption,
  shopOwnerRoleId,
  UserRequest,
  viewerRoleId
} from './InviteUserStore.types'
import { getInitialUserRequest } from './InviteUserStore.utils'

export const INVITE_USER_STORE = 'INVITE_USER_STORE'

export class InviteUserStore {
  private _rootStore: RootStore

  constructor (rootStore: RootStore) {
    this._rootStore = rootStore
  }

  @observable user: UserRequest = getInitialUserRequest()
  @observable private _userRolesMap: Map<number, UserRoleDTO> = new Map()
  @observable private _people: Map<number, PersonDTO> = new Map()
  @observable private _brands: Map<number, BrandDTO> = new Map()
  @observable private _projects: Map<number, ProjectDTO> = new Map()
  @observable private _linkToEntityId: number = null

  @observable private _assignedProjectIds: Set<number> = new Set()

  @action setUserProps = (props: Partial<UserRequest>) => {
    this.user = {
      ...this.user,
      ...props
    }
  }

  @action private _setUserRoles = (userRolesMap: Map<number, UserRoleDTO>) => {
    this._userRolesMap = userRolesMap
  }

  @action private _setPeople = (people: Map<number, PersonDTO>) => {
    this._people = people
  }

  @action private _setBrands = (brands: Map<number, BrandDTO>) => {
    this._brands = brands
  }

  @action private _setProjects = (projects: Map<number, ProjectDTO>) => {
    this._projects = projects
  }

  @action setLinkToEntityId = (entityId: number) => {
    this._linkToEntityId = entityId
  }

  @action private _clearAssignedProjects = () => {
    this._assignedProjectIds = new Set()
  }

  @action assignProject = (projectId: number) => {
    this._assignedProjectIds.add(projectId)
    this._refreshAssignedProjectTable()
  }

  private _refreshAssignedProjectTable = () => {
    const {
      [PROJECTS_TABLE_STORE]: {
        // setSearchResults,
        triggerSearch
      },
      [PROJECT_STORE]: {
        setProjectsList
      }
    } = this._rootStore

    setProjectsList(
      Array.from(this._assignedProjectIds.values())
        .map(id => new Project(
          this.getProjectById(id)
        ))
    )
    triggerSearch()
  }

  getUserRoles = () => {
    const {
      setLoadingOn,
      setLoadingOff,
      handleApiError
    } = this._rootStore[GENERAL_STORE]

    setLoadingOn()
    Api
      .get<UserRoleDTO[]>('/config/roles', 'userApi')
      .then(data => {
        const userRolesMap = new Map<number, UserRoleDTO>()
        data.forEach(role => {
          if (role.id === viewerRoleId) return
          userRolesMap.set(role.id, role)
        })
        this._setUserRoles(userRolesMap)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  getUserRoleById = (userRoleId: number): UserRoleDTO => {
    return this._userRolesMap.get(userRoleId)
  }

  onUserRoleSelect = (roleId: number) => {
    const selectedRole = this._userRolesMap.get(roleId)
    this.setUserProps({ roles: [selectedRole] })
    this.setLinkToEntityId(null)

    switch (true) {
      case this._isPersonEditRoleSelected: {
        this.loadPeople()
        break
      }
      case this._isShopOwnerRoleSelected: {
        this.loadBrands()
        break
      }
      default: {
        this.loadProjects()
        break
      }
    }
  }

  getLinkToEntitiesMap = (isPersonEditRoleSelected: boolean): Map<number, any> => {
    return isPersonEditRoleSelected
      ? this._people
      : this._brands
  }

  onGoToLinkedEntityPageClick = () => {
    const {
      push
    } = this._rootStore[GENERAL_STORE]

    const route = this.linkToEntityType === LinkTo.PERSON
      ? 'persons'
      : 'brands/edit'
    push(`/${route}/${this._linkToEntityId}`)
  }

  sendInvitation = () => {
    if (!this.isFormValid) return

    const {
      setLoadingOn,
      setLoadingOff,
      handleApiError,
      push,
      showSuccess
    } = this._rootStore[GENERAL_STORE]

    setLoadingOn()
    Api
      .post<UserDTO>('/user/invite', this.user, 'userApi')
      .then(invitedUser => {
        if (this.isLinkToEntityRoleSelected) {
          const route = this.linkToEntityType === LinkTo.PERSON
            ? 'people'
            : 'brands'
          return Api.post(
            `/permissions/users/${invitedUser.id}/${route}/${this._linkToEntityId}`,
            undefined
          )
        } else if (this.hasAssignedProjects) {
          return Promise.all(
            Array.from(this._assignedProjectIds.values())
              .map(projectId =>
                Api.post(
                  `/permissions/users/${invitedUser.id}/projects/${projectId}`,
                  undefined
                )
              )
          )
        } else {
          setLoadingOff()
        }
      })
      .then(() => {
        push('/user-management')
        showSuccess('Invitation sent.')
        this.clearStore()
      })
      .catch(handleApiError)
  }

  clearStore = () => {
    this.setUserProps(getInitialUserRequest())
    this._setUserRoles(new Map())
    this._setPeople(new Map())
    this._setBrands(new Map())
    this._setProjects(new Map())
    this.setLinkToEntityId(null)
    this._clearAssignedProjects()
  }

  loadPeople = () => {
    if (this._people.size) return

    const {
      setLoadingOn,
      setLoadingOff,
      handleApiError
    } = this._rootStore[GENERAL_STORE]

    setLoadingOn()
    Api
      .get<PersonDTO[]>('/person/all')
      .then(data => {
        const peopleMap = new Map<number, PersonDTO>()
        data.forEach(p => peopleMap.set(p.id, p))
        this._setPeople(peopleMap)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  loadBrands = () => {
    if (this._brands.size) return

    const {
      setLoadingOn,
      setLoadingOff,
      handleApiError
    } = this._rootStore[GENERAL_STORE]

    setLoadingOn()
    Api
      .get<BrandDTO[]>('/brand/all')
      .then(data => {
        const brandsMap = new Map<number, BrandDTO>()
        data.forEach(entity => brandsMap.set(entity.id, entity))
        this._setBrands(brandsMap)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  loadProjects = () => {
    if (this._projects.size) return

    const {
      setLoadingOn,
      setLoadingOff,
      handleApiError
    } = this._rootStore[GENERAL_STORE]

    setLoadingOn()
    Api
      .get<ProjectDTO[]>('/project/all')
      .then(data => {
        const projectsMap = new Map<number, ProjectDTO>()
        data.forEach(entity => projectsMap.set(entity.id, entity))
        this._setProjects(projectsMap)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  getProjectById = (projectId: number): ProjectDTO => {
    return this._projects.get(projectId)
  }

  @computed get userRoleOptions (): SelectOption<number>[] {
    return Array.from(this._userRolesMap.values()).map(role => ({
      label: role.label,
      value: role.id
    }))
  }

  @computed private get _selectedUserRole (): UserRoleDTO {
    if (!this.user) return null
    const { roles } = this.user
    if (!roles || !roles.length) return null

    return roles[0]
  }

  @computed get selectedUserRoleId (): number {
    if (!this._selectedUserRole) return '' as any
    return this._selectedUserRole.id
  }

  @computed private get _isPersonEditRoleSelected (): boolean {
    if (!this.selectedUserRoleId) return false
    return this.selectedUserRoleId === personEditRoleId
  }

  @computed private get _isShopOwnerRoleSelected (): boolean {
    if (!this.selectedUserRoleId) return false
    return this.selectedUserRoleId === shopOwnerRoleId
  }

  @computed get selectedLinkToEntityId (): number {
    if (!this._linkToEntityId) return '' as any
    return this._linkToEntityId
  }

  @computed get isLinkToEntityRoleSelected (): boolean {
    return this._isPersonEditRoleSelected ||
      this._isShopOwnerRoleSelected
  }

  @computed get linkToEntityType (): string {
    switch (true) {
      case this._isPersonEditRoleSelected: {
        return LinkTo.PERSON
      }
      case this._isShopOwnerRoleSelected: {
        return LinkTo.STORE
      }
      default: return ' '
    }
  }

  @computed get linkToEntityOptions (): SelectOption<number, ImageLabelLineProps>[] {
    const entityMap: Map<number, any> = this._isPersonEditRoleSelected
      ? this._people
      : this._brands
    return Array.from(entityMap.values()).map(entity => ({
      label: entity.name,
      value: entity.id,
      customComponentProps: {
        imgSrc: getMainImageSrcForObj(entity),
        label: entity.name
      }
    }))
  }

  @computed get canAssignProjects (): boolean {
    if (!this.selectedUserRoleId) return false
    return !roleIdsWithNoProjectsAssignmentOption.includes(this.selectedUserRoleId)
  }

  @computed get isFormValid (): boolean {
    const {
      emailAddress
    } = this.user

    if (
      !isEmailValid(emailAddress) ||
      !this.selectedUserRoleId
    ) return false

    return true
  }

  @computed get projectOptions (): ProjectDTO[] {
    return Array.from(this._projects.values())
  }

  @computed get hasAssignedProjects (): boolean {
    return this._assignedProjectIds.size > 0
  }
}
